import _ from 'underscore';
import { useMemo, useCallback, useState } from 'react';

import { CampaignType as AccountLevelCampaignType } from '@bingads-webui-campaign/campaign-ui-common';

import { getPreference, getPreferencePatcher, getPagePreferenceKey } from './preference-helper';

const CampaignLevelCampaignType = _.mapObject(AccountLevelCampaignType, (val, key) => key);

function fetchDashboardId({
  preference, campaignType, navigationLevel, serverContext,
}) {
  const name = 'default';
  const preferenceData = preference.findByNameAtUserLevel('AccountsOverviewAccountMode');
  if ((preferenceData && preferenceData.Data === 'smart') || serverContext.CurrentCustomer.HasExpertAccount === false) {
    return `${name}-${navigationLevel}-smart`;
  } else if (campaignType === CampaignLevelCampaignType.Hotel || campaignType === AccountLevelCampaignType.Hotel) {
    return `${name}-${navigationLevel}-hotel`;
  } else if (campaignType === CampaignLevelCampaignType.PerformanceMax || campaignType === AccountLevelCampaignType.PerformanceMax) {
    return `${name}-${navigationLevel}-performancemax`;
  } else if (campaignType === CampaignLevelCampaignType.App || campaignType === AccountLevelCampaignType.App) {
    return `${name}-${navigationLevel}-app`;
  }
  return `${name}-${navigationLevel}`;
}

function getLegacyPreference({
  preference, campaignType, navigationLevel, serverContext,
}) {
  const prefs = navigationLevel === 'customer' ? preference.findByNameAtUserLevel('accountsDashboard/preferences') : preference.findByNameAtAccountLevel('dashboard/preferences');
  const dashboardId = fetchDashboardId({
    preference, campaignType, navigationLevel, serverContext,
  });
  return _.get(prefs, ['Data', dashboardId, 'cards']);
}

function getAvailableCardsConfigs({
  cardsConfigs,
  navigationLevel,
  campaignType,
  isSDK,
}) {
  return _.filter(cardsConfigs, cardConfig => cardsConfigs.isValidForNavigationLevel(cardConfig, navigationLevel, campaignType, isSDK));
}

function getSelectedCardsConfigs({
  availableCardsConfigs,
  selectedCards,
}) {
  if (_.isArray(selectedCards)) {
    return _.filter(availableCardsConfigs, cardConfig => !_.get(_.find(selectedCards, sc => sc.id === cardConfig.typeId), 'hidden'));
  }
  return availableCardsConfigs;
}

export function getInitSelectedCardsConfig({
  preference,
  cardsConfigs,
  navigationLevel,
  campaignType,
  serverContext,
  isSDK,
}) {
  const pagePreferenceKey = getPagePreferenceKey({
    navigationLevel,
    campaignType,
  });
  const pagePreference = getPreference({
    key: pagePreferenceKey,
    preference,
  }) || {};

  const legacyCardSelectionPreference = getLegacyPreference({
    preference, campaignType, navigationLevel, serverContext,
  });

  const selectedCards = pagePreference.selectedCards || legacyCardSelectionPreference;

  const availableCardsConfigs = getAvailableCardsConfigs({
    cardsConfigs,
    navigationLevel,
    campaignType,
    isSDK,
  });

  const selectedCardsConfigs = getSelectedCardsConfigs({
    availableCardsConfigs,
    selectedCards,
  });

  return selectedCardsConfigs;
}

export const useSelectedCardsWithPreference = ({
  preference,
  cardsConfigs,
  navigationLevel,
  campaignType,
  serverContext,
  isLoadingWelcomeCardConfig,
  isSDK,
}) => {
  const pagePreferenceKey = getPagePreferenceKey({
    navigationLevel,
    campaignType,
  });

  const pagePreference = useMemo(() => getPreference({
    key: pagePreferenceKey,
    preference,
  }) || {}, [pagePreferenceKey, preference]);

  const patchPagePreference = useMemo(() => getPreferencePatcher({
    key: pagePreferenceKey,
    preference,
  }), [pagePreferenceKey, preference]);

  const { selectedCards: initSelectedCards } = pagePreference;
  const legacyCardSelectionPreference = getLegacyPreference({
    preference, campaignType, navigationLevel, serverContext,
  });
  const [selectedCards, setSelectedCards] = useState(initSelectedCards || legacyCardSelectionPreference);

  const availableCardsConfigs = useMemo(
    () => getAvailableCardsConfigs({
      cardsConfigs,
      navigationLevel,
      campaignType,
      isSDK,
    }),
    [campaignType, cardsConfigs, navigationLevel, isSDK]
  );

  const selectedCardsConfigs = useMemo(() => getSelectedCardsConfigs({
    availableCardsConfigs,
    selectedCards,
  }), [availableCardsConfigs, selectedCards]);

  const cardSelectionCards = useMemo(() => {
    if (!_.isArray(selectedCards)) {
      return _.map(availableCardsConfigs, cardConfig => ({ id: cardConfig.typeId, hidden: false }));
    }
    return _.map(availableCardsConfigs, cardConfig => ({ id: cardConfig.typeId, hidden: _.get(_.find(selectedCards, sc => sc.id === cardConfig.typeId), 'hidden') }));
  }, [availableCardsConfigs, selectedCards]);

  const onCardSelectionSaved = useCallback((selectionToSave) => {
    patchPagePreference({ selectedCards: selectionToSave });
    setSelectedCards(selectionToSave);
  }, [patchPagePreference]);

  if (isLoadingWelcomeCardConfig) {
    return {
      selectedCardsConfigs: [],
      cardSelectionCards: [],
      onCardSelectionSaved: _.noop,
    };
  }

  return {
    selectedCardsConfigs,
    cardSelectionCards,
    onCardSelectionSaved,
  };
};
